import { retrieveLaunchParams } from '@telegram-apps/sdk';

/**
 * Retrieve Telegram start parameter.
 */
export function getStartParam(): string | null {
  try {
    // SDK 3.x names it tgWebAppStartParam (there is no `startParam` key);
    // the signed init data carries the same value as start_param.
    const launchParams = retrieveLaunchParams();
    const startParam =
      launchParams.tgWebAppStartParam ?? launchParams.tgWebAppData?.start_param;
    if (startParam && typeof startParam === 'string' && startParam.length > 0) {
      return startParam;
    }
  } catch (_e) {
    // ignore: retrieveLaunchParams may throw outside Telegram Mini App
  }
  return null;
}

export const INVITE_START_PARAM_PREFIX = 'mm-';

export const INVITE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const INVITE_TOKEN_PATTERN = /^[0-9A-Za-z]{20}$/;
const PENDING_INVITE_KEY = 'mm_invite';
const DONE_INVITES_KEY = 'mm_invite_done';
const DONE_INVITES_LIMIT = 20;

export type PendingInvite = {
  token: string;
  opened: boolean;
  savedAt: number;
};

export type InviteStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const resolveStorage = (): InviteStorage | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const readJson = (storage: InviteStorage, key: string): unknown => {
  try {
    const raw = storage.getItem(key);

    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeJson = (storage: InviteStorage, key: string, value: unknown) => {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    void 0;
  }
};

const removeKey = (storage: InviteStorage, key: string) => {
  try {
    storage.removeItem(key);
  } catch {
    void 0;
  }
};

const isPendingInvite = (value: unknown): value is PendingInvite =>
  typeof value === 'object' &&
  value !== null &&
  'token' in value &&
  typeof value.token === 'string' &&
  'opened' in value &&
  typeof value.opened === 'boolean' &&
  'savedAt' in value &&
  typeof value.savedAt === 'number';

const readDoneTokens = (storage: InviteStorage): string[] => {
  const done = readJson(storage, DONE_INVITES_KEY);

  return Array.isArray(done)
    ? done.filter((token): token is string => typeof token === 'string')
    : [];
};

export const captureInviteStartParam = (
  raw: string | null | undefined,
  storage: InviteStorage,
  now = Date.now(),
) => {
  if (typeof raw !== 'string' || !raw.startsWith(INVITE_START_PARAM_PREFIX)) {
    return;
  }

  const token = raw.slice(INVITE_START_PARAM_PREFIX.length);

  if (!INVITE_TOKEN_PATTERN.test(token)) return;

  if (readDoneTokens(storage).includes(token)) return;

  const pending = readJson(storage, PENDING_INVITE_KEY);

  if (isPendingInvite(pending) && pending.token === token) return;

  writeJson(storage, PENDING_INVITE_KEY, {
    token,
    opened: false,
    savedAt: now,
  } satisfies PendingInvite);
};

export const readPendingInvite = (
  storage: InviteStorage | null = resolveStorage(),
  now = Date.now(),
): PendingInvite | null => {
  if (!storage) return null;

  const pending = readJson(storage, PENDING_INVITE_KEY);

  if (!isPendingInvite(pending)) return null;

  const isExpired = now - pending.savedAt > INVITE_TTL_MS;

  if (isExpired || readDoneTokens(storage).includes(pending.token)) {
    removeKey(storage, PENDING_INVITE_KEY);
    return null;
  }

  return pending;
};

export const markInviteOpened = (
  token: string,
  storage: InviteStorage | null = resolveStorage(),
) => {
  const pending = readPendingInvite(storage);

  if (!storage || !pending || pending.token !== token || pending.opened) {
    return;
  }

  writeJson(storage, PENDING_INVITE_KEY, { ...pending, opened: true });
};

export const finishInvite = (
  token: string,
  storage: InviteStorage | null = resolveStorage(),
) => {
  if (!storage) return;

  const done = readDoneTokens(storage).filter((each) => each !== token);

  writeJson(
    storage,
    DONE_INVITES_KEY,
    [...done, token].slice(-DONE_INVITES_LIMIT),
  );

  const pending = readJson(storage, PENDING_INVITE_KEY);

  if (isPendingInvite(pending) && pending.token === token) {
    removeKey(storage, PENDING_INVITE_KEY);
  }
};

const visitedInviteTokens = new Set<string>();

export const markInviteRouteVisited = (
  storage: InviteStorage | null = resolveStorage(),
) => {
  const pending = readPendingInvite(storage);

  if (pending) visitedInviteTokens.add(pending.token);
};

const isInviteRedirectDue = (storage: InviteStorage | null) => {
  const pending = readPendingInvite(storage);

  return pending !== null && !visitedInviteTokens.has(pending.token);
};

export const launchRoute = (
  storage: InviteStorage | null = resolveStorage(),
) => (isInviteRedirectDue(storage) ? '/invite' : '/dating');

export const unregisteredRoute = (
  storage: InviteStorage | null = resolveStorage(),
) => (isInviteRedirectDue(storage) ? '/invite' : '/sign-up');

export const nextRouteAfterAuth = (
  storage: InviteStorage | null = resolveStorage(),
) => {
  const pending = readPendingInvite(storage);

  if (!pending) return '/dating';

  return pending.opened ? '/invite/candidate' : '/invite';
};
